import http from "V2.0/services/httpService";

const endPoint: string = "/assistants";

const ROUTES = {
  ASSISTANTS: "/assistants",
  DUPLICATE_ASSISTANT: "/assistants/whitelabelDuplicateAssistant",

  /* PUT */
  MODEL_SETTINGS: "/assistants/whitelabelModelSettings",
  PROMPT_SETTINGS: "/assistants/whitelabelPromptSettings",
  RAG_SETTINGS: "/assistants/whitelabelRagSettings",
  EXTRACTIONS: "/assistants/whitelabelextractions",
  REAL_TIME_BOOKINGS: "/assistants/whitelabelRealTimeBookingSettings",
  CUSTOM_APIS: "assistants/whitelabelCustomApis",
  TIMEZONE_SETTINGS: "assistants/whitelabelTimezoneSettings",
  TRANSFER_SETTINGS: "assistants/whitelabelTransferSettings",
  MESSAGING_SETTINGS: "assistants/whitelabelMessagingSettings",
  COMMUNICATION_BINDINGS: "/assistants/whitelabelCommunicationBindings",
  VOICE_SETTINGS: "assistants/whitelabelVoiceSettings",
  TRANSCRIPTION_SETTINGS: "assistants/whitelabelTranscriptionSettings",
  LANGUAGE_SETTINGS: "assistants/whitelabelLanguageSettings",
  TTS_SETTINGS: "assistants/whitelabelTtsSettings",
  TTS_VOCABULARY: "assistants/whitelabelTTSVocabulary",

  /* PUT POST DELETE */
  CUSTOM_ACTIONS: "assistants/whitelabelCustomActions",
  AGENTS: "assistants/whitelabelAgents",
  ASSISTANT_AGENTS: "assistants/whitelabelAssistantAgents",
  CUSTOM_APIS_ARRAY: "assistants/whitelabelCustomApisArray",
  CUSTOM_ACTIONS_ARRAY: "assistants/whitelabelCustomActionsArray",

  ASSISTANT_COACH: "assistants/whitelabelAssistantCoachSettings",
};

function duplicateAssistant({ workspaceId, assistantId, payload }) {
  return http.post(
    `${ROUTES.DUPLICATE_ASSISTANT}/${workspaceId}/${assistantId}`,
    payload
  );
}

function getAssistants({ projectId }) {
  return http.get(`${endPoint}/whitelabelGetAssistants/${projectId}`);
}

function createAssistant({
  projectId,
  payload,
}: {
  projectId: string;
  payload: { name: string; description: string; assistantType: string };
}) {
  return http.post(
    `${endPoint}/whitelabelCreateAssistant/${projectId}`,
    payload
  );
}

function removeAssistant({ workspaceId, assistantId }) {
  return http.delete(
    `${ROUTES.ASSISTANTS}/whitelabelDeleteAssistant/${workspaceId}/${assistantId}`
  );
}

function getAssistant({ workspaceId, assistantId }) {
  return http.get(
    `${endPoint}/whitelabelGetAssistant/${workspaceId}/${assistantId}`
  );
}

function updateAssistant({ projectId, assistantId, payload }) {
  return http.put(
    `${endPoint}/whitelabelUpdateAssistant/${projectId}/${assistantId}`,
    payload
  );
}

function getModelSettings({ workspaceId, assistantId }) {
  return http.get(`${ROUTES.MODEL_SETTINGS}/${workspaceId}/${assistantId}`);
}

function putModelSettings({ workspaceId, assistantId, payload }) {
  return http.put(
    `${ROUTES.MODEL_SETTINGS}/${workspaceId}/${assistantId}`,
    payload
  );
}

function putCommunicationBinding({
  workspaceId,
  assistantId,
  payload,
}: {
  workspaceId: string;
  assistantId: string;
  payload: CommunicationBinding;
}) {
  return http.put(
    `${ROUTES.COMMUNICATION_BINDINGS}/${workspaceId}/${assistantId}`,
    payload
  );
}

function deleteCommunicationBinding({
  workspaceId,
  assistantId,
  communicationBindingId,
}) {
  return http.delete(
    `${ROUTES.COMMUNICATION_BINDINGS}/${workspaceId}/${assistantId}/${communicationBindingId}`
  );
}

function putTTSSettings({ workspaceId, assistantId, payload }) {
  return http.put(
    `${ROUTES.TTS_SETTINGS}/${workspaceId}/${assistantId}`,
    payload
  );
}

function putTranscriptionSettings({ workspaceId, assistantId, payload }) {
  return http.put(
    `${ROUTES.TRANSCRIPTION_SETTINGS}/${workspaceId}/${assistantId}`,
    payload
  );
}

function getLanguageSettings({ workspaceId, assistantId }) {
  return http.get(`${ROUTES.LANGUAGE_SETTINGS}/${workspaceId}/${assistantId}`);
}

function putLanguageSettings({ workspaceId, assistantId, payload }) {
  return http.put(
    `${ROUTES.LANGUAGE_SETTINGS}/${workspaceId}/${assistantId}`,
    payload
  );
}

function getTtsVocabulary({ workspaceId, assistantId }) {
  return http.get(`${ROUTES.TTS_VOCABULARY}/${workspaceId}/${assistantId}`);
}

// Replaces the entire vocabulary with `payload.ttsVocabulary` ({ word: replacement }).
function putTtsVocabulary({ workspaceId, assistantId, payload }) {
  return http.put(
    `${ROUTES.TTS_VOCABULARY}/${workspaceId}/${assistantId}`,
    payload
  );
}

// Adds/updates entries; `payload.ttsVocabulary` ({ word: replacement }) is merged
// into the existing vocabulary (case-insensitive on the word).
function patchTtsVocabulary({ workspaceId, assistantId, payload }) {
  return http.patch(
    `${ROUTES.TTS_VOCABULARY}/${workspaceId}/${assistantId}`,
    payload
  );
}

// Removes entries; `payload.words` (string[]) is deleted from the vocabulary.
// axios needs the body under `data` for DELETE requests.
function deleteTtsVocabulary({ workspaceId, assistantId, payload }) {
  return http.delete(
    `${ROUTES.TTS_VOCABULARY}/${workspaceId}/${assistantId}`,
    {
      headers: { "Content-Type": "application/json" },
      data: payload,
    }
  );
}

function putCustomApis({ workspaceId, assistantId, payload }) {
  return http.put(
    `${ROUTES.CUSTOM_APIS}/${workspaceId}/${assistantId}`,
    payload
  );
}

function postCustomApisArray({ workspaceId, assistantId, payload }) {
  return http.post(
    `${ROUTES.CUSTOM_APIS_ARRAY}/${workspaceId}/${assistantId}`,
    payload
  );
}

function getCustomApisArray({ workspaceId, assistantId }) {
  return http.get(`${ROUTES.CUSTOM_APIS_ARRAY}/${workspaceId}/${assistantId}`);
}

function deleteCustomApisArray({ workspaceId, assistantId, customApiId }) {
  return http.delete(
    `${ROUTES.CUSTOM_APIS_ARRAY}/${workspaceId}/${assistantId}/${customApiId}`
  );
}

function putCustomApisArray({
  workspaceId,
  assistantId,
  customApiId,
  payload,
}) {
  return http.put(
    `${ROUTES.CUSTOM_APIS_ARRAY}/${workspaceId}/${assistantId}/${customApiId}`,
    payload
  );
}

function putExtraction({ workspaceId, assistantId, payload }) {
  return http.put(
    `${ROUTES.EXTRACTIONS}/${workspaceId}/${assistantId}`,
    payload
  );
}

function putCustomActions({ workspaceId, assistantId, payload }) {
  return http.put(
    `${ROUTES.CUSTOM_ACTIONS}/${workspaceId}/${assistantId}`,
    payload
  );
}

function putCustomActionsArray({
  workspaceId,
  assistantId,
  customActionId,
  payload,
}) {
  return http.put(
    `${ROUTES.CUSTOM_ACTIONS_ARRAY}/${workspaceId}/${assistantId}/${customActionId}`,
    payload
  );
}

function deleteCustomActionsArray({
  workspaceId,
  assistantId,
  customActionId,
}) {
  return http.delete(
    `${ROUTES.CUSTOM_ACTIONS_ARRAY}/${workspaceId}/${assistantId}/${customActionId}`
  );
}

function postCustomActionsArray({ workspaceId, assistantId, payload }) {
  return http.post(
    `${ROUTES.CUSTOM_ACTIONS_ARRAY}/${workspaceId}/${assistantId}`,
    payload
  );
}

function putVoiceSettings({ workspaceId, assistantId, payload }) {
  return http.put(
    `${ROUTES.VOICE_SETTINGS}/${workspaceId}/${assistantId}`,
    payload
  );
}

function putMessagingSettings({ workspaceId, assistantId, payload }) {
  return http.put(
    `${ROUTES.MESSAGING_SETTINGS}/${workspaceId}/${assistantId}`,
    payload
  );
}

function putTimezoneSetting({ workspaceId, assistantId, payload }) {
  return http.put(
    `${ROUTES.TIMEZONE_SETTINGS}/${workspaceId}/${assistantId}`,
    payload
  );
}

function putTransferSettings({ workspaceId, assistantId, payload }) {
  return http.put(
    `${ROUTES.TRANSFER_SETTINGS}/${workspaceId}/${assistantId}`,
    payload
  );
}

function getAgents({ workspaceId, assistantId }) {
  return http.get(`${ROUTES.AGENTS}/${workspaceId}/${assistantId}`);
}

function postAgent({ workspaceId, assistantId, payload }) {
  return http.post(`${ROUTES.AGENTS}/${workspaceId}/${assistantId}`, payload);
}

function putAgent({ workspaceId, assistantId, agentId, payload }) {
  return http.put(
    `${ROUTES.AGENTS}/${workspaceId}/${assistantId}/${agentId}`,
    payload
  );
}

function deleteAgent({ workspaceId, assistantId, agentId }) {
  return http.delete(
    `${ROUTES.AGENTS}/${workspaceId}/${assistantId}/${agentId}`
  );
}

function getAssistantAgents({ workspaceId, assistantId }) {
  return http.get(`${ROUTES.ASSISTANT_AGENTS}/${workspaceId}/${assistantId}`);
}

function postAssistantAgent({ workspaceId, assistantId, payload }) {
  return http.post(
    `${ROUTES.ASSISTANT_AGENTS}/${workspaceId}/${assistantId}`,
    payload
  );
}

function putAssistantAgent({ workspaceId, assistantId, agentId, payload }) {
  return http.put(
    `${ROUTES.ASSISTANT_AGENTS}/${workspaceId}/${assistantId}/${agentId}`,
    payload
  );
}

function deleteAssistantAgent({ workspaceId, assistantId, agentId }) {
  return http.delete(
    `${ROUTES.ASSISTANT_AGENTS}/${workspaceId}/${assistantId}/${agentId}`
  );
}

function putRAGSettings({ workspaceId, assistantId, payload }) {
  return http.put(
    `${ROUTES.RAG_SETTINGS}/${workspaceId}/${assistantId}`,
    payload
  );
}

function putRealTimeBookings({ workspaceId, assistantId, payload }) {
  return http.put(
    `${ROUTES.REAL_TIME_BOOKINGS}/${workspaceId}/${assistantId}`,
    payload
  );
}
function postUploadReferenceDocument({ workspaceId, assistantId, payload }) {
  return http.post(
    `${ROUTES.MESSAGING_SETTINGS}/uploadReferenceDoc/${workspaceId}/${assistantId}`,
    payload,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );
}

function deleteReferenceDocument({ workspaceId, assistantId, payload }) {
  return http.delete(
    `${ROUTES.MESSAGING_SETTINGS}/deleteReferenceDoc/${workspaceId}/${assistantId}`,
    {
      headers: { "Content-Type": "application/json" },
      data: payload,
    }
  );
}

function updateReferenceDocumentPrompt({ workspaceId, assistantId, payload }) {
  return http.put(
    `${ROUTES.MESSAGING_SETTINGS}/updateReferencePrompt/${workspaceId}/${assistantId}`,
    payload
  );
}

function updateConversationReminderSettings({
  workspaceId,
  assistantId,
  payload,
}) {
  return http.post(
    `${ROUTES.MESSAGING_SETTINGS}/whitelabelConversationReminderSettings/${workspaceId}/${assistantId}`,
    payload,
  );
}


function updateAssistantCoachSettings({ workspaceId, assistantId, payload }) {
  return http.put(
    `${ROUTES.ASSISTANT_COACH}/${workspaceId}/${assistantId}`,
    payload,
  );
}

export {
  /* Assistants */
  getAssistants,
  createAssistant,
  duplicateAssistant,
  removeAssistant,
  getAssistant,
  updateAssistant,

  /* Model Settings */
  getModelSettings,
  putModelSettings,

  /* TTS Settings */
  putTTSSettings,

  /* Transcription Settings */
  putTranscriptionSettings,

  /* Language Settings */
  getLanguageSettings,
  putLanguageSettings,

  /* TTS Vocabulary */
  getTtsVocabulary,
  putTtsVocabulary,
  patchTtsVocabulary,
  deleteTtsVocabulary,

  /* Communication binding */
  putCommunicationBinding,
  deleteCommunicationBinding,

  /* Custom APIs */
  putCustomApis,

  /* Custom APIs array */
  postCustomApisArray,
  putCustomApisArray,
  deleteCustomApisArray,
  getCustomApisArray,

  /* Extraction */
  putExtraction,

  /* Custom Actions */
  putCustomActions,
  deleteCustomActionsArray,
  putCustomActionsArray,
  postCustomActionsArray,

  /* Voice Settings */
  putVoiceSettings,

  /* MessagingSettings */
  putMessagingSettings,

  /* Timezone settings */
  putTimezoneSetting,

  /* Transfer Settings */
  putTransferSettings,

  /* Agents */
  getAgents,
  postAgent,
  putAgent,
  deleteAgent,

  /* Assistant Agents */
  getAssistantAgents,
  postAssistantAgent,
  putAssistantAgent,
  deleteAssistantAgent,

  /* RAG Settings */
  putRAGSettings,

  /* Real Time Bookings */
  putRealTimeBookings,

  /* Document Collection */
  postUploadReferenceDocument,
  deleteReferenceDocument,
  updateReferenceDocumentPrompt,

  /* Conversation Reminder */
  updateConversationReminderSettings,

  /* Assistant Coach */
  updateAssistantCoachSettings
};
